import { presetVersionFilePath } from "@pax/api-contract/presets/v1alpha1";
import { DRAFT_RECORDS_FILE, parseDraftRecords } from "@pax/draft";
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { presetVersionQuery } from "@/features/presets/queries";
import { orpc } from "@/lib/api";
import { paxFetch } from "@/lib/network";

export const editingQuery = (slug: string) =>
  orpc.presets.editing.get.queryOptions({ input: { params: { slug } }, staleTime: 10_000 });

export const teamQuery = (slug: string) =>
  orpc.presets.team.get.queryOptions({ input: { params: { slug } }, staleTime: 10_000 });

export const sharesQuery = (slug: string) =>
  orpc.presets.shares.get.queryOptions({ input: { params: { slug } }, staleTime: 10_000 });

export const statsQuery = (slug: string) =>
  orpc.presets.stats.get.queryOptions({ input: { params: { slug } }, staleTime: 60_000 });

export const myPresetsQuery = () => orpc.presets.mine.list.queryOptions({ staleTime: 10_000 });

/** A version's detail, for its read-only page and for where a draft began. */
export function useVersion(versionId: string | null) {
  return useQuery({ ...presetVersionQuery(versionId ?? ""), enabled: versionId !== null });
}

/** The records a published version's draft held, which never change, to read it by. */
export function useVersionRecords(versionId: string | null) {
  return useQuery({
    queryKey: ["preset-versions", versionId, "records"],
    enabled: versionId !== null,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async () => {
      const response = await paxFetch(presetVersionFilePath(versionId ?? "", DRAFT_RECORDS_FILE));
      if (!response.ok) throw new Error(`The map did not load (${response.status}).`);
      return parseDraftRecords(await response.text());
    },
  });
}

/** Everything about the presets you work on, once any of it changed. */
export function refreshPresets(queryClient: QueryClient) {
  return queryClient.invalidateQueries({ queryKey: orpc.presets.key() });
}
